import {Component, inject, OnInit} from '@angular/core';
import {AbstractControl, UntypedFormBuilder, UntypedFormGroup} from '@angular/forms';
import {ActivatedRoute, Router} from '@angular/router';
import {SymbolsService} from '../../services/symbols.service';
import {UtilsService} from '../../appSharedModule/services/utils.service';
import {TableService} from '../../services/table.service';
import * as _ from 'lodash';
import {saveAs} from 'file-saver';
import {HttpResponse} from '@angular/common/http';
import {DatePipe} from '@angular/common';
import {SelectItem} from 'primeng/api';
import {AuthenticationService} from '../../authenticationModule/services/authentication.service';
import {QedSymbol} from '../../models/qedSymbol';
import {QedSymbolsInfo} from '../../models/qedSymbolsInfo';
import {BaseClientComponent} from '../base-client.component';

@Component({
  selector: 'app-qed-symbol-data',
  templateUrl: './qed-symbol-data.component.html',
  styleUrls: ['./qed-symbol-data.component.scss'],
  host: {class: 'sym-page'}
})
export class QedSymbolDataComponent extends BaseClientComponent implements OnInit {

  researchForm: UntypedFormGroup;
  listQedSymbols: QedSymbol[];
  filteredQedSymbols: QedSymbol[];
  marketList = [];
  cols = [];
  qedSymbolsInfo: QedSymbolsInfo;
  qedSymbolsInfoData: any;
  alertQedSymbolHasNotChanged = false;
  qedProductDropdown: SelectItem[];
  minDateForCalendar: Date = new Date('2015-01-01');
  maxDateForCalendar: Date = null;
  startDates: string[];
  dateArray: any[];
  disabledDatesInCalendar: any[];
  canSeeNextWeekSymbols = false;
  nextWeekSymbolInfo: QedSymbolsInfo;
  loading = true;
  isOnNextWeekMode = false;
  currentQedSymbolsList: QedSymbol[];
  nextWeekSymbolList: QedSymbol[] = [];
  currentQedSymbolsInfo: QedSymbolsInfo;
  isQfd = false;
  isQhf = false;
  service = 'qed';
  displayPastListsDialog = false;
  private readonly route = inject(ActivatedRoute);
  userChoicesFiltersDefaultQedSymbols = {
    qedProduct: '',
    qfdProduct: '',
    channel: '',
    assetClass: ''
  };
  userChoicesActiveFilters = _.clone(this.userChoicesFiltersDefaultQedSymbols);
  error = false;
  errorMessage = '';
  displayHelpPopUp = false;

  constructor(private symbolsService: SymbolsService,
              private utilsService: UtilsService,
              public datePipe: DatePipe,
              public tableService: TableService,
              private router: Router,
              private formBuilder: UntypedFormBuilder,
              private authenticationService: AuthenticationService) {
    super();
  }

  get qedProductForm(): AbstractControl {
    return this.getForm('qedProduct');
  }

  get channelForm(): AbstractControl {
    return this.getForm('channel');
  }

  get qfdProductForm(): AbstractControl {
    return this.getForm('qfdProduct');
  }

  get assetClassForm(): AbstractControl {
    return this.getForm('assetClass');
  }

  get pastDateRange1Form(): AbstractControl {
    return this.getForm('pastDateRange1');
  }

  get pastDateRange2Form(): AbstractControl {
    return this.getForm('pastDateRange2');
  }

  get currentUser() {
    return this.authenticationService.userCurrent;
  }

  get canDownloadNextQFDSymbolList(): boolean {
    return this.isQfd && this.canSeeNextWeekSymbols;
  }

  get dialogHeader(): string {
    return this.isQfd && !this.canDownloadNextQFDSymbolList ? 'Download not available' : 'Download with API';
  }

  ngOnInit() {
    const response$ = this.authenticationService.waitForAuthenticationThenLaunch(() => {
      this.initData();
    });
    this.subscriptions.push(response$);
  }

  private initData() {
    this.isQhf = !!this.route.snapshot.data['qhfOnly'];
    if (this.router.url.includes('qfd')) {
      this.isQfd = true;
      this.service = 'qfd';
    }
    this.maxDateForCalendar = new Date();
    this.maxDateForCalendar.setMonth(this.maxDateForCalendar.getMonth() + 1);
    this.initializeColumns();
    this.userChoicesActiveFilters = this.utilsService.loadUserPref('qedSymbolsFilter', this.userChoicesFiltersDefaultQedSymbols);
    this.initializeResearchForm(this.userChoicesActiveFilters);
    this.getQedSymbolStartDate();
    this.utilsService.lockUi();
  }

  public getDatesArray() {
    this.dateArray = [];
    const currentDate = this.minDateForCalendar;
    const stopDate = this.maxDateForCalendar;
    while (currentDate <= stopDate) {
      this.dateArray.push(this.datePipe.transform(currentDate, 'yyyy-MM-dd'));
      currentDate.setDate(currentDate.getDate() + 1);
    }
  }

  trackByFn(index: number, qedSymbol: QedSymbol): number {
    return qedSymbol.symbolId ?? index;
  }

  redirectionToNotFound() {
    this.utilsService.redirectToNotFound();
  }

  resetTable(): void {
    this.qedProductForm.setValue('');
    this.channelForm.setValue('');
    this.assetClassForm.setValue('');
    this.filteredQedSymbolList();
    // The list of clients sent by the server is ordered by id
    this.listQedSymbols.sort((symbol1, symbol2) => symbol1.symbolId - symbol2.symbolId);
  }

  removeDuplicates(list) {
    const uniqueList = {};
    list.forEach(function (i) {
      if (!uniqueList[i]) {
        uniqueList[i] = true;
      }
    });
    return Object.keys(uniqueList);
  }

  createQedProductNames() {
    if (!this.listQedSymbols) {
      return;
    }
    const list = this.listQedSymbols
      .filter(s => s.qedProduct)
      .map(function (qedSymbol) {
        return qedSymbol.qedProduct;
      }).sort();
    const qedProducts = _.uniq(list);
    this.qedProductDropdown = [];
    _.forEach(qedProducts, (productName: string) => {
      this.qedProductDropdown.push({
        label: productName,
        value: productName,
      });
    });
    return qedProducts;
  }

  qedSymbolList(market: string): QedSymbol[] {
    if (!this.listQedSymbols) {
      return [];
    }
    return this.listQedSymbols.filter(qedSymbol => qedSymbol.marketName === market && this.filter(qedSymbol));

  }

  filter(qedSymbol) {
    return this.hasQedProduct(qedSymbol, this.qedProductForm.value)
      && this.hasChannel(qedSymbol, this.channelForm.value)
      && this.hasQfdProduct(qedSymbol, this.qfdProductForm.value)
      && this.hasAssetClass(qedSymbol, this.assetClassForm.value);
  }

  hasQedProduct(qedSymbol: QedSymbol, qedProductFilterValue) {
    if (qedProductFilterValue === '' || qedProductFilterValue === null) {
      return true;
    }
    return qedSymbol.qedProduct === qedProductFilterValue;
  }

  hasQfdProduct(qedSymbol: QedSymbol, qfdProductFilterValue) {
    if (qfdProductFilterValue === '' || qfdProductFilterValue === null) {
      return true;
    }
    return !!qedSymbol.product.toLowerCase().includes(qfdProductFilterValue.toLowerCase());
  }

  hasChannel(qedSymbol: QedSymbol, channelFilterValue) {
    if (channelFilterValue === '' || channelFilterValue === null) {
      return true;
    }
    if (this.isQfd) {
      return !!qedSymbol.qedChannelId.name.toLowerCase().includes(channelFilterValue.toLowerCase());
    }
    return !!qedSymbol.market.toLowerCase().includes(channelFilterValue.toLowerCase());
  }

  hasAssetClass(qedSymbol: QedSymbol, assetClassFilterValue) {
    if (assetClassFilterValue === '' || assetClassFilterValue === null) {
      return true;
    }
    return qedSymbol.assetClass && !!qedSymbol.assetClass.toLowerCase().includes(assetClassFilterValue.toLowerCase());
  }

  filteredQedSymbolList() {
    if (!this.listQedSymbols) {
      return [];
    }
    this.utilsService.saveUserData('qedSymbolsFilter',
      {
        'qedProduct': this.qedProductForm.value,
        'channel': this.channelForm.value,
        'assetClass': this.assetClassForm.value
      });
    this.filteredQedSymbols = this.listQedSymbols.filter(qedSymbol => this.filter(qedSymbol));

  }

  noDataToDisplay() {
    for (const market of this.marketList) {
      if (this.qedSymbolList(market).length !== 0) {
        return false;
      }
    }
    return true;
  }

  redirectionToQedMulticastPageWithGetParameter(channelName, multicastAddress) {
    const base = this.isQhf ? '/#/qhf-symbols/multicast' : '/#/qed-symbols/multicast';
    if (!!channelName) {
      location.assign(base + '?channel=' + channelName);
    }
    if (!!multicastAddress) {
      location.assign(base + '?multicast_group=' + multicastAddress);
    }
  }

  getUatValuesForYFromQedSymbolInfoData() {
    if (!this.qedSymbolsInfoData) {
      return 'Data not available';
    }
    let data = '';
    // tslint:disable-next-line:forin
    for (const cpt in this.qedSymbolsInfoData.uat) {
      data += this.qedSymbolsInfoData.uat[cpt].id + '=' + this.qedSymbolsInfoData.uat[cpt].name + ', ';
    }
    return data.slice(0, data.length - 2);
  }

  getCommentFromQedSymbolInfoData() {
    return (this.qedSymbolsInfoData && this.qedSymbolsInfoData.comment) ? this.qedSymbolsInfoData.comment : '';
  }

  rangeDateNotSelected() {
    return !this.pastDateRange1Form.value
      || !this.pastDateRange2Form.value;
  }

  verifyDateSelected(rangeDate1 = false, rangeDate2 = false) {
    if (!this.pastDateRange1Form.value || !this.pastDateRange2Form.value) {
      return;
    }
    this.symbolsService.verifyDateSelected(rangeDate1, rangeDate2, this.pastDateRange1Form, this.pastDateRange2Form);
  }

  resetRangeDates() {
    this.pastDateRange1Form.setValue('');
    this.pastDateRange2Form.setValue('');
  }

  downloadCurrentQdSymbolList(includingInstrumentId = false) {
    this.symbolsService.getClientSymbolsList(this.service, includingInstrumentId).subscribe({
      next: (response) => {
        const blob = this.symbolsService.b64toBlob(response['file_content'], response['mime_type'], null);
        saveAs(blob, this.applyQhfFilename(response['file_name']));
        this.error = false;
      }, error: () => {
        this.utilsService.displayMessage('The symbol list could not be downloaded', 'Please try again later.', 'error');
      }
    });
  }

  private applyQhfFilename(filename: string): string {
    return this.isQhf ? filename.replace(/^qed/, 'qhf') : filename;
  }

  downloadNextWeekQfdSymbolList() {
    this.symbolsService.getNextWeekClientSymbolsList(this.service).subscribe({
      next: (response) => {
        const blob = this.symbolsService.b64toBlob(response['file_content'], response['mime_type'], null);
        saveAs(blob, response['file_name']);
      }, error: () => {
        this.utilsService.displayMessage('The symbol list could not be downloaded', 'Please try again later.', 'error');
      }
    });
  }

  downloadQedSymbolsWithThisDateRange() {
    const startDate = new Date(this.pastDateRange1Form.value);
    const endDate = new Date(this.pastDateRange2Form.value);
    const startDateAsString = this.datePipe.transform(startDate, 'yyyy-MM-dd');
    const endDateAsString = this.datePipe.transform(endDate, 'yyyy-MM-dd');
    this.symbolsService.getClientSymbolsListByDate(startDateAsString, endDateAsString, this.service)
      .subscribe({
        next: (response) => {
          const blob = this.symbolsService.b64toBlob(response['file_content'], response['mime_type'], null);
          saveAs(blob, this.applyQhfFilename(response['file_name']));
        }, error: (error: HttpResponse<any>) => {
          this.utilsService.handleErrorWebservice$(error);
        }
      });
  }

  displayNextWeekSymbols() {
    this.currentQedSymbolsList = this.listQedSymbols;
    this.currentQedSymbolsInfo = this.qedSymbolsInfo;
    this.qedSymbolsInfo = this.nextWeekSymbolInfo;
    if (!this.nextWeekSymbolList || this.nextWeekSymbolList.length === 0) {
      this.getQedSymbolList(this.nextWeekSymbolInfo);
    } else {
      this.listQedSymbols = this.nextWeekSymbolList;
      this.createMarketNames();
    }
    this.isOnNextWeekMode = true;
    this.qedSymbolsInfoData = JSON.parse(this.qedSymbolsInfo.data);

    this.getCommentFromQedSymbolInfoData();
  }

  returnToCurrentSymbolList() {
    this.nextWeekSymbolList = this.listQedSymbols;
    this.listQedSymbols = this.currentQedSymbolsList;
    this.nextWeekSymbolInfo = this.qedSymbolsInfo;
    this.qedSymbolsInfo = this.currentQedSymbolsInfo;
    this.qedSymbolsInfoData = JSON.parse(this.qedSymbolsInfo.data);
    this.createMarketNames();
    this.getCommentFromQedSymbolInfoData();
    this.isOnNextWeekMode = false;
  }

  alterDisplayHelpPopUp() {
    this.displayHelpPopUp = !this.displayHelpPopUp;
  }

  htmlDecode(qedSymbol: any) {
    let decoded = qedSymbol;
    let previous;
    do {
      previous = decoded;
      decoded = this.utilsService.htmlDecode(previous);
    } while (decoded !== previous);
    return decoded;
  }

  private initializeColumns() {
    if (this.isQfd) {
      this.cols = [
        {field: 'qedProduct', header: 'QFD product'},
        {field: 'market.name', header: 'Channel'},
        {field: 'assetClass', header: 'Asset class'},
        {field: 'product', header: 'Product'},
        {field: 'name', header: 'Name'},
        {field: 'maturity', header: 'Maturity'},
        {field: 'qedSymbol', header: 'QFD symbol'},
        {field: 'qfdId', header: 'QFD Id'},
        {field: 'removalDate', header: 'Removal date'}
      ];
      return;
    }
    const code = this.isQhf ? 'QHF' : 'QED';
    this.cols = [
      {field: 'marketName', header: 'Market'},
      {field: 'qedProduct', header: code + ' product'},
      {field: 'market.name', header: 'Channel'},
      {field: 'assetClass', header: 'Asset class'},
      {field: 'product', header: 'Product'},
      {field: 'name', header: 'Name'},
      {field: 'instrumentId', header: 'Instrument ID'},
      {field: 'maturity', header: 'Maturity'},
      {field: 'multicastAdress', header: 'Multicast address'},
      {field: 'qedSymbol', header: code + ' symbol'}
    ];
  }

  private getQedSymbolStartDate() {
    this.symbolsService.getSymbolStartDates(this.service).subscribe({
      next: qedSymbolsStartDates => {
        this.startDates = qedSymbolsStartDates;
        this.getDatesArray();
        const disabledDatesStrings = this.dateArray.filter(item => this.startDates.indexOf(item) < 0);
        this.disabledDatesInCalendar = disabledDatesStrings.map(date => new Date(date));
        this.getQedSymbolsInfo();
      }, error: () => {
        this.utilsService.unlockUi();
        this.loading = false;
        this.utilsService.displayMessage('The symbol list could not be loaded', 'Please try again later.', 'error');
      }
    });
  }

  private getQedSymbolsInfo() {
    if (!this.startDates || !this.startDates[0]) {
      this.alertQedSymbolHasNotChanged = true;
      this.utilsService.unlockUi();
      this.loading = false;
      return;
    }
    this.getCurrentSymbolInfos();
    this.getNextWeekSymbolInfos();
  }

  private getCurrentSymbolInfos() {
    this.symbolsService.getSymbolInfoBetweenDates(0, this.service).subscribe({
        next: qedSymbolsInfo => {
          if (qedSymbolsInfo.length > 0) {
            this.qedSymbolsInfo = qedSymbolsInfo[0];
            this.qedSymbolsInfoData = JSON.parse(this.qedSymbolsInfo.data);
            this.getQedSymbolList(this.qedSymbolsInfo);
            return;
          } else {
            this.getSymbolInfoWithFirstStartDate();
          }
        },
        error: () => {
          this.alertQedSymbolHasNotChanged = true;
          this.utilsService.unlockUi();
          this.loading = false;
        }
      }
    );
  }

  private getNextWeekSymbolInfos() {
    this.symbolsService.getSymbolInfoBetweenDates(1, this.service).subscribe(
      res => {
        if (res.length === undefined) {
          return;
        }
        res = res.filter(info => new Date(info.startDate) > new Date());
        if (res.length > 0) {
          this.canSeeNextWeekSymbols = true;
          this.nextWeekSymbolInfo = res[0];
        }
      });
  }

  private getSymbolInfoWithFirstStartDate() {
    this.symbolsService.getQedSymbolInfoWithStartDate(this.startDates[0], this.service).subscribe({
        next: qrdSymbolsInfo => {
          if (qrdSymbolsInfo.length === 1) {
            this.qedSymbolsInfo = qrdSymbolsInfo[0];
            this.qedSymbolsInfoData = JSON.parse(this.qedSymbolsInfo.data);
            this.getQedSymbolList(this.qedSymbolsInfo);
          } else {
            this.alertQedSymbolHasNotChanged = true;
            this.utilsService.unlockUi();
            this.loading = false;
          }
        }, error: () => {
          this.alertQedSymbolHasNotChanged = true;
          this.utilsService.unlockUi();
          this.loading = false;
        }
      }
    );
  }

  private getQedSymbolList(qedSymbolsInfo: QedSymbolsInfo) {
    this.symbolsService.getSymbolList(qedSymbolsInfo.startDate, this.service, this.isQhf).subscribe({
        next: res => {
          this.listQedSymbols = res;
          this.filteredQedSymbolList();
          if (this.listQedSymbols.length !== 0) {
            this.createMarketNames();
            this.createQedProductNames();
          } else {
            this.alertQedSymbolHasNotChanged = true;
          }
          this.utilsService.unlockUi();
          this.loading = false;
        },
        error: () => {
          this.redirectionToNotFound();
          this.utilsService.unlockUi();
          this.loading = false;
        }
      }
    );
  }

  private initializeResearchForm(userChoicesActiveFilters) {
    this.researchForm = this.formBuilder.group({
      qedProduct: [userChoicesActiveFilters.qedProduct],
      qfdProduct: [userChoicesActiveFilters.qfdProduct],
      channel: [userChoicesActiveFilters.channel],
      assetClass: [userChoicesActiveFilters.assetClass],
      pastDateRange1: [''],
      pastDateRange2: ['']
    });
    this.subscriptions.push(
      this.pastDateRange1Form.valueChanges.subscribe(() => this.verifyDateSelected(true)),
      this.pastDateRange2Form.valueChanges.subscribe(() => this.verifyDateSelected(false, true))
    );
  }

  private getForm(formName: string): AbstractControl {
    return this.researchForm.get(formName);
  }

  private createMarketNames() {
    let list: any[];
    list = this.listQedSymbols
      .map(qedSymbol => qedSymbol.marketName)
      .sort();
    this.marketList = this.removeDuplicates(list);
  }
}
